import {
  SurveyEndpointRecord,
  useOrganisationList,
  useSiteList,
  useSurveyEndpoints,
} from "@inrange/building-manager-api-client";
import { getApiHostname } from "@inrange/building-manager-api-client/config";
import { useState } from "react";
import { Button, Form, Spinner, Table } from "react-bootstrap";
import Select from "react-select";
import getCustomerAppDomain from "../../utils/getCustomerAppDomain";
import SurveyEndpointLanguageSelector from "../../components/SurveyEndpointLanguageSelector";
import { SurveyProjectRow } from "../../components/SurveyProjectRow";
import CollapsibleCard from "../site/CollapsibleCard";

interface SiteOption {
  id: string;
  name: string;
}

interface OrgOption {
  id: string;
  name: string;
}

interface SelectOption {
  value: string;
  label: string;
}

type OrgSelectOption = SelectOption;

const selectStyles = {
  control: (base: Record<string, unknown>) => ({
    ...base,
    minHeight: 31,
    fontSize: 14,
  }),
  valueContainer: (base: Record<string, unknown>) => ({
    ...base,
    padding: "0 6px",
  }),
  input: (base: Record<string, unknown>) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  dropdownIndicator: (base: Record<string, unknown>) => ({ ...base, padding: 4 }),
  clearIndicator: (base: Record<string, unknown>) => ({ ...base, padding: 4 }),
  // Render the open menu in a portal at the document body so the table's
  // overflow:auto wrapper doesn't clip it.
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }),
};

const ProjectsList = ({
  endpoint,
  siteOptions,
  sitesLoading,
  apiHostname,
  customerAppDomain,
}: {
  endpoint: SurveyEndpointRecord;
  siteOptions: SiteOption[];
  sitesLoading: boolean;
  apiHostname: string;
  customerAppDomain: string;
}) => {
  const projects = endpoint.projects || [];
  if (projects.length === 0) {
    return (
      <div className="text-muted small p-2">No projects for this endpoint.</div>
    );
  }
  return (
    <div style={{ maxHeight: "400px", overflowY: "auto" }}>
      <Table size="sm" className="mb-0">
        <thead
          style={{
            position: "sticky",
            top: 0,
            backgroundColor: "var(--bs-body-bg)",
            zIndex: 1,
          }}
        >
          <tr>
            <th>Project</th>
            <th>Survey</th>
            <th>Cost sheet</th>
            <th>BMA Site</th>
            <th>Updated</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {projects.map((p) => (
            <SurveyProjectRow
              key={p.id}
              endpointId={endpoint.id}
              project={p}
              siteOptions={siteOptions}
              sitesLoading={sitesLoading}
              apiHostname={apiHostname}
              customerAppDomain={customerAppDomain}
              endpointName={endpoint.name}
              endpointShortcode={endpoint.shortcode}
              projectNameHref={null}
              selectInputIdPrefix="site-selector-"
              selectMinWidth={280}
              selectStyles={selectStyles}
            />
          ))}
        </tbody>
      </Table>
    </div>
  );
};

const EpcSelector = ({
  endpointId,
  currentOrgId,
  orgOptions,
  usedOrgIds,
  orgsLoading,
}: {
  endpointId: string;
  currentOrgId: string | null;
  orgOptions: OrgOption[];
  usedOrgIds: Set<string>;
  orgsLoading: boolean;
}) => {
  const { updateEndpointOrg } = useSurveyEndpoints();
  const [error, setError] = useState<string | null>(null);

  const selectOptions: OrgSelectOption[] = orgOptions
    .filter((o) => o.id === currentOrgId || !usedOrgIds.has(o.id))
    .map((o) => ({ value: o.id, label: o.name }));
  const selected = currentOrgId
    ? selectOptions.find((o) => o.value === currentOrgId) || null
    : null;

  const handleChange = async (next: OrgSelectOption | null) => {
    setError(null);
    const nextOrgId = next?.value ?? null;
    if (nextOrgId === currentOrgId) return;
    try {
      await updateEndpointOrg.mutateAsync({ endpointId, orgId: nextOrgId });
    } catch (err) {
      setError(String(err));
    }
  };

  const isPending =
    updateEndpointOrg.isLoading &&
    updateEndpointOrg.variables?.endpointId === endpointId;

  return (
    <div style={{ minWidth: 240, maxWidth: 360 }}>
      <Select
        inputId={`epc-selector-${endpointId}`}
        options={selectOptions}
        value={selected}
        onChange={(next) => handleChange(next as OrgSelectOption | null)}
        placeholder={orgsLoading ? "Loading EPCs..." : "No EPC selected"}
        isDisabled={orgsLoading || isPending}
        isClearable
        styles={selectStyles}
        menuPortalTarget={document.body}
      />
      {isPending && (
        <div className="text-muted small mt-1">
          <Spinner animation="border" size="sm" /> Updating...
        </div>
      )}
      {error && <div className="text-danger small mt-1">{error}</div>}
    </div>
  );
};

const SurveyEndpointsCard = () => {
  const { fetchSurveyEndpoints, createEndpoint, deleteEndpoint } =
    useSurveyEndpoints();
  const { siteList, siteListQuery } = useSiteList();
  const { fetchOrganisations } = useOrganisationList();
  const [newName, setNewName] = useState("");
  const [newOrgId, setNewOrgId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const endpoints: SurveyEndpointRecord[] =
    fetchSurveyEndpoints.data?.endpoints || [];

  const siteOptions: SiteOption[] = (siteList || [])
    .map((s) => ({ id: s.id, name: s.name }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const epcOptions: OrgOption[] = (
    fetchOrganisations.data?.organisations || []
  )
    .filter((o) => o.orgType === "EPC")
    .map((o) => ({ id: o.id, name: o.name }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const usedEpcIds = new Set(
    endpoints
      .map((ep) => ep.org_id)
      .filter((id): id is string => id !== null)
  );

  const availableEpcOptions = epcOptions.filter((o) => !usedEpcIds.has(o.id));

  const orgsLoading = fetchOrganisations.isLoading;
  const selectedNewOrg = newOrgId
    ? availableEpcOptions.find((o) => o.id === newOrgId) || null
    : null;

  const handleCreate = async () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    if (trimmed.includes("-")) {
      setError("Name must not contain dashes");
      return;
    }
    if (!newOrgId) {
      setError("Select an EPC org");
      return;
    }
    setError(null);
    try {
      await createEndpoint.mutateAsync({ name: trimmed, orgId: newOrgId });
      setNewName("");
      setNewOrgId(null);
    } catch (err) {
      setError(String(err));
    }
  };

  const handleDelete = async (endpoint: SurveyEndpointRecord) => {
    if (
      !window.confirm(
        `Delete survey endpoint "${endpoint.name}"? This will also delete all projects + submissions.`
      )
    )
      return;
    try {
      await deleteEndpoint.mutateAsync(endpoint.id);
    } catch (err) {
      setError(String(err));
    }
  };

  const customerDomain = getCustomerAppDomain();
  const apiHostname = getApiHostname();
  const getSurveyUrl = (ep: SurveyEndpointRecord) =>
    `${customerDomain}/survey/${ep.name}-${ep.shortcode}`;

  return (
    <CollapsibleCard title="Survey endpoints" initialOpen={true}>
      <div className="d-flex gap-2 mb-3 align-items-end flex-wrap">
        <Form.Group style={{ minWidth: 240 }}>
          <Form.Label>EPC organisation</Form.Label>
          <Select
            inputId="new-endpoint-epc"
            options={availableEpcOptions.map((o) => ({
              value: o.id,
              label: o.name,
            }))}
            value={
              selectedNewOrg
                ? { value: selectedNewOrg.id, label: selectedNewOrg.name }
                : null
            }
            onChange={(next) =>
              setNewOrgId((next as OrgSelectOption | null)?.value ?? null)
            }
            placeholder={orgsLoading ? "Loading EPCs..." : "Select an EPC..."}
            isDisabled={orgsLoading}
            isClearable
            styles={selectStyles}
            menuPortalTarget={document.body}
          />
        </Form.Group>
        <Form.Group style={{ flex: 1, minWidth: 240 }}>
          <Form.Label>Surveyor company name (used in survey URL)</Form.Label>
          <Form.Control
            type="text"
            size="sm"
            placeholder="e.g. surveycorp"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
          />
        </Form.Group>
        <Button
          variant="primary"
          size="sm"
          onClick={handleCreate}
          disabled={createEndpoint.isLoading || !newName.trim() || !newOrgId}
        >
          {createEndpoint.isLoading ? "Creating..." : "Create"}
        </Button>
      </div>
      {error && <div className="text-danger mb-2">{error}</div>}

      {fetchSurveyEndpoints.isLoading && <div>Loading...</div>}

      {fetchSurveyEndpoints.isError && (
        <div className="text-danger mb-2">
          Failed to load survey endpoints.{" "}
          <Button
            variant="link"
            size="sm"
            className="p-0 text-danger"
            onClick={() => fetchSurveyEndpoints.refetch()}
          >
            Retry
          </Button>
        </div>
      )}

      {endpoints.length > 0 && (
        <div className="d-flex flex-column gap-3">
          {endpoints.map((ep) => (
            <div
              key={ep.id}
              className="border rounded p-3"
              style={{ background: "#fafafa" }}
            >
              <div className="d-flex justify-content-between align-items-start mb-2 gap-3 flex-wrap">
                <div style={{ flex: 1, minWidth: 240 }}>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="fw-bold">{ep.name}</span>
                    <span className="text-muted">-</span>
                    <EpcSelector
                      endpointId={ep.id}
                      currentOrgId={ep.org_id}
                      orgOptions={epcOptions}
                      usedOrgIds={usedEpcIds}
                      orgsLoading={orgsLoading}
                    />
                    <SurveyEndpointLanguageSelector
                      endpointId={ep.id}
                      currentLanguage={ep.default_language ?? null}
                    />
                  </div>
                  <div className="small mt-1">
                    Created {new Date(ep.created_at).toLocaleDateString()}
                  </div>
                  <div className="small">
                    <a
                      href={getSurveyUrl(ep)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {getSurveyUrl(ep)}
                    </a>
                  </div>
                </div>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleDelete(ep)}
                  disabled={deleteEndpoint.isLoading}
                >
                  Delete
                </Button>
              </div>
              <ProjectsList
                endpoint={ep}
                siteOptions={siteOptions}
                sitesLoading={siteListQuery.isLoading}
                apiHostname={apiHostname}
                customerAppDomain={customerDomain}
              />
            </div>
          ))}
        </div>
      )}

      {!fetchSurveyEndpoints.isLoading && endpoints.length === 0 && (
        <div className="text-muted">No survey endpoints created yet.</div>
      )}
    </CollapsibleCard>
  );
};

export default SurveyEndpointsCard;
